import './PlaylistReport.css'

import { MdInfo as InfoIcon } from '@react-icons/all-files/md/MdInfo'
import * as React from 'react'

import {
  getPlaylistReport,
  toggleReportColumn,
  toggleReportColumnCustomFields,
} from '@disco/state-management/actions/ReportActions'
import type { PlaylistReportState } from '@disco/state-management/reducers/reports/playlistReport'
import { getMetadataNameForKey } from '@disco/utils/metadata'
import { REPORT_LIMIT } from '@disco/utils/report'

import type { CustomField } from 'types/business'
import type { Group } from 'types/group'
import type { DropdownOption } from 'types/ui'

import DownloadLink from 'components/common/download-link'
import Row from 'components/common/row'
import MetadataCheckbox from 'components/common/tracks/metadata-fields/metadata-checkbox'
import MetadataSelect from 'components/common/tracks/metadata-fields/metadata-select'
import DocumentHeadTags from 'components/document-head-tags'
import AdminTool from 'components/ui/admin-tool'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import ChoiceList from 'components/ui/choice-list'
import ContactUsLink from 'components/ui/contact-us-link'
import DatePicker from 'components/ui/date-picker'
import Heading from 'components/ui/heading'
import InlineLink from 'components/ui/inline-link'
import Label from 'components/ui/label'
import ModalWindow from 'components/ui/modal-window'
import PageContainer from 'components/ui/page-container'
import PricingBadge from 'components/ui/pricing-badge'
import TextField from 'components/ui/text-field'

import ReportChannels from './report-channels'
import ReportCheckbox from './report-checkbox'
import ReportContact from './report-contact'
import ReportDate from './report-date'
import ReportDownloadButton from './report-download-button'
import ReportPreview from './report-preview'
import ReportSelect from './report-select'
import ReportTags from './report-tags'
import ReportUser from './report-user'
import ReportWriter from './report-writer'
import ReportsDisabledAlert from './ReportsDisabledAlert'
import ReportsLimitedAlert from './ReportsLimitedAlert'
import ReportsTrialAlert from './ReportsTrialAlert'

const EN_SPACE = ' '
const TRACK_COLUMNS = [
  ['track_name', 'Title'],
  ['album', 'Album'],
  ['artist', 'Artist'],
  ['bpm', 'BPM'],
  ['comment', 'Comment'],
  ['composer', 'Composer'],
  ['genre', 'Genre'],
  ['grouping', 'Grouping'],
  ['year', 'Year'],
  ['pro', 'PRO Number'],
  ['isrc', 'ISRC'],
  [
    'track_id',
    'DISCO Track ID',
    'Include the DISCO track ID if you’d like to update the metadata on these tracks via spreadsheet and re-import.',
  ],
  ['internal_comment', 'Internal note'],
  ['client_comment', 'Client note'],
]
type Props = {
  channels: Group[]
  territories: Group[]
  teamMember: boolean
  playlistReportRequest: PlaylistReportState['report']
  visibleColumns: Array<string>
  metadataOptions: DropdownOption[]
  loadGroups: (...args: Array<any>) => any
  resetPlaylistReportParams: (...args: Array<any>) => any
  updatePlaylistReportParams: (...args: Array<any>) => any
  sendMixpanelEvent: (...args: Array<any>) => any
  getPlaylistReport: typeof getPlaylistReport
  form: PlaylistReportState['form']
  toggleReportColumn: typeof toggleReportColumn
  toggleReportColumnCustomFields: typeof toggleReportColumnCustomFields
  // trackCheckboxRowLength: number,
  insightsDisabled?: boolean
  isTrial: boolean
  isFeatureLimited: boolean | null | undefined
  isFeatureEnabled: boolean | null | undefined
  badgeTitle: string
  customFields: CustomField[][] | null | undefined
  customFieldsColumns: string[]
  downloadUrl: string | null | undefined
}

class PlaylistReport extends React.Component<
  Props,
  {
    modalOpen: boolean
    showAllCustomFields: boolean
    selectedCustomField: CustomField | null | undefined
  }
> {
  state: {
    modalOpen: boolean
    selectedCustomField: CustomField | null | undefined
    showAllCustomFields: boolean
  } = {
    modalOpen: false,
    showAllCustomFields: false,
    selectedCustomField: null,
  }

  componentDidMount() {
    this.props.resetPlaylistReportParams()
    this.props.loadGroups()
    this.props.updatePlaylistReportParams({
      advancedMetadataKey: '',
    })
  }

  _errorNode: HTMLElement | null | undefined

  componentDidUpdate(prevProps: Props) {
    if (
      this.props.playlistReportRequest.generated &&
      this.props.playlistReportRequest.generated !==
        prevProps.playlistReportRequest.generated
    ) {
      this._openModal() // if (this._resultNode && this._resultNode.scrollIntoView) {
      // this._resultNode.scrollIntoView({ block: 'start', behavior: 'smooth' })
      // }
    }

    if (
      this.props.playlistReportRequest.generationError &&
      this.props.playlistReportRequest.generationError !==
        prevProps.playlistReportRequest.generationError
    ) {
      if (
        this._errorNode &&
        'scrollIntoView' in this._errorNode &&
        this._errorNode.scrollIntoView
      ) {
        this._errorNode.scrollIntoView({
          block: 'start',
          behavior: 'smooth',
        })
      }
    }
  }

  _getPlaylistReport: () => void = () => {
    this._openModal()

    this.props.getPlaylistReport('json_table')
  }
  _openIntercom: () => void = () => {
    this.props.sendMixpanelEvent('Open contact support')
  }
  _handleCheckboxChange: (key: string, value: boolean) => void = (
    key: string,
    value: boolean,
  ) => {
    if (!key.includes('custom_fields')) {
      this.props.toggleReportColumn(key, value)
    }
  }
  _handleInputChange: (
    event:
      | any
      | {
          target: {
            name: string
            value: any
          }
        }
      | {
          target: {
            name: string
            value: string
          }
        }
      | {
          target: {
            name: string
            value: null | Array<string>
          }
        },
  ) => void = (event: any) => {
    const { name, value } = event.target
    this.props.updatePlaylistReportParams({
      [name]: value,
    })
  }
  _openModal: () => void = () =>
    this.setState({
      modalOpen: true,
    })
  _closeModal: () => void = () =>
    this.setState({
      modalOpen: false,
    })

  _renderError(generationError: any): React.ReactElement<'div'> {
    let message

    if (generationError.status >= 500) {
      message = (
        <div>
          Your report is taking a little time to process. Please try your search
          again:
          <ul>
            <li>be more specific</li>
            <li>avoid vague query like ”a”</li>
          </ul>
        </div>
      )
    } else {
      message = <div>Error has occurred. Try again later.</div>
    }

    return (
      <div
        ref={n => (this._errorNode = n)}
        className="Page_Form is-next PlaylistReport_Error"
      >
        {message}
      </div>
    )
  }

  _renderResults(): React.ReactNode {
    const { results } = this.props.playlistReportRequest
    if (!results) return
    let header = null
    let actions = <Button caption="Close" onClick={this._closeModal} />
    let message = null

    if ('data' in results) {
      header =
        results.data.rows.length === 0 ? (
          <React.Fragment>
            <Heading>Sorry! No results for this one.</Heading>
            <div>Try broadening your report search a little.</div>
          </React.Fragment>
        ) : (
          <Heading>Report preview</Heading>
        )
      actions = <ReportDownloadButton />
    }

    if ('text' in results) {
      const returned = results.rowCount || 0
      const exceeds = returned >= REPORT_LIMIT

      switch (results.text) {
        case 'REPORT_GENERATING':
          header = <Heading>Your report is being generated</Heading>
          message = `We're generating your report now, and will email it to you as soon as it's ready. Please note, during peak periods, reports can take up to an hour to generate.`
          break

        case 'REPORT_DOWNLOADING':
          header = <Heading>Your report is downloading</Heading>
          message = (
            <div>
              Your report should now download automatically. If it doesn’t,{' '}
              <ContactUsLink onClick={this._openIntercom}>
                let us know.
              </ContactUsLink>
              {exceeds ? (
                <div className="PlaylistReport_ExceedMessage">
                  <InfoIcon className="PlaylistReport_ExceedIcon" />
                  Reports are limited to {returned} results. This report has
                  more than that.
                  <br />
                  The first {returned} results are downloading.
                </div>
              ) : null}
            </div>
          )
          break

        default:
          // never happens
          // eslint-disable-next-line no-unused-expressions
          break
      }
    }
    return (
      <ModalWindow
        header={header}
        isOpen={this.state.modalOpen}
        actions={actions}
        onClose={this._closeModal}
      >
        {'data' in results ? <ReportPreview data={results.data} /> : null}
        {message}
      </ModalWindow>
    )
  }

  renderAlert(): null | React.ReactElement<'div'> {
    const { isTrial, isFeatureLimited, isFeatureEnabled } = this.props

    if (isTrial) {
      return (
        <div className="PlaylistReport_Alert">
          <ReportsTrialAlert />
        </div>
      )
    }

    if (isFeatureLimited) {
      return (
        <div className="PlaylistReport_Alert">
          <ReportsLimitedAlert />
        </div>
      )
    }

    if (!isFeatureLimited && !isFeatureEnabled) {
      return (
        <div className="PlaylistReport_Alert">
          <ReportsDisabledAlert />
        </div>
      )
    }

    return null
  }

  _setSelectedCustomField: (key: string) => void = (key: string) => {
    const { customFields } = this.props
    let result = null
    customFields?.find(row => {
      result = row.find(item => item.key === key)
      return result
    })
    this.setState({
      selectedCustomField: result,
    })
  }
  _getFieldComponent: (isDisabledAllContent: boolean) => React.ReactNode = (
    isDisabledAllContent: boolean,
  ) => {
    const { selectedCustomField: field } = this.state
    const { advancedMetadataKey, advancedMetadataValue, generalMetadataValue } =
      this.props.form
    const name =
      advancedMetadataKey === ''
        ? 'generalMetadataValue'
        : 'advancedMetadataValue'
    const value =
      advancedMetadataKey === '' ? generalMetadataValue : advancedMetadataValue
    const placeholder =
      advancedMetadataKey === ''
        ? 'Search metadata'
        : 'Search ' + getMetadataNameForKey(advancedMetadataKey).toLowerCase()
    const defaultField = (
      <TextField
        disabled={isDisabledAllContent}
        name={name}
        value={value}
        placeholder={placeholder}
        onChange={this._handleInputChange}
      />
    )

    if (!field) {
      return defaultField
    }

    switch (field.type) {
      case 'dropdown_multi':
      case 'dropdown_single':
        return (
          <MetadataSelect
            placeholder="Select..."
            customField={field}
            onChange={value => {
              this._handleInputChange({
                target: {
                  name,
                  value,
                },
              })
            }}
            field={{
              value,
            }}
            disabled={false}
          />
        )

      case 'date':
        return (
          <DatePicker
            value={value}
            onChange={value => {
              this._handleInputChange({
                target: {
                  name,
                  value,
                },
              })
            }}
          />
        )

      case 'boolean':
        return (
          <MetadataCheckbox
            className="PlaylistReport_Checkbox"
            key={field.key} // If field is disabled for editing, it doesn’t mean that user can’t filter by specific field values.
            field={{ ...field, editable: true }}
            onChange={(label, checked) => {
              this._handleInputChange({
                target: {
                  name,
                  value: checked,
                },
              })
            }}
            checked={typeof value !== 'boolean' ? Boolean(value) : value}
          />
        )

      default:
        return defaultField
    }
  }

  render(): React.ReactNode {
    const {
      channels,
      territories,
      visibleColumns,
      form,
      playlistReportRequest,
      isFeatureLimited,
      isFeatureEnabled,
      isTrial,
      badgeTitle,
      customFieldsColumns,
    } = this.props
    const { generationError, generated, generating } = playlistReportRequest
    const isDisabledAllContent =
      !isTrial && !isFeatureLimited && !isFeatureEnabled
    const isReportChannelsDisabled = !isFeatureEnabled
    const isFiltersDisabled = !(isFeatureLimited || isFeatureEnabled)
    const isShowBadge = isTrial || isFeatureLimited
    const DEFAULT_SHOW_FIELDS = 5

    /*eslint no-unused-expressions: 0*/
    let customFieldsFormatted = []
    this.props.customFields?.forEach(row => {
      row.forEach(item => {
        customFieldsFormatted.push({
          label: item.text,
          value: `custom_fields ${item.text}`,
          selected: customFieldsColumns.includes(item.key),
          disabled: isReportChannelsDisabled,
          onChange: value => {
            this.props.toggleReportColumnCustomFields(item.key, value)
          },
        })
      })
    })
    customFieldsFormatted = this.state.showAllCustomFields
      ? customFieldsFormatted
      : customFieldsFormatted.slice(0, DEFAULT_SHOW_FIELDS)
    const trackInformation = [
      ...TRACK_COLUMNS.map(([id, name, tooltipContent]) => ({
        label: name,
        value: id,
        tooltipContent: tooltipContent,
        selected: visibleColumns.includes(id),
        disabled: isReportChannelsDisabled,
      })),
      ...customFieldsFormatted,
    ]
    return (
      <React.Fragment>
        <DocumentHeadTags title="Reports | DISCO" />
        {!this.props.insightsDisabled && this.renderAlert()}
        {this.props.downloadUrl && (
          <DownloadLink url={this.props.downloadUrl} />
        )}
        <PageContainer>
          <PageContainer.Section
            disabled={isDisabledAllContent}
            title="Reports"
          >
            <Row columnsSize={[4, 4, 4]} marginBottom>
              <div>
                <Label caption="Select date range" />
                <ReportDate disabled={isDisabledAllContent} />
              </div>
              <div>
                <Label caption="Select metadata" />
                <div className="PlaylistReport_Container">
                  <div className="PlaylistReport_Before">
                    <ReportSelect
                      formKey="advancedMetadataKey"
                      disabled={isDisabledAllContent}
                      value={this.props.form.advancedMetadataKey || ''}
                      onChangeMain={this._setSelectedCustomField}
                      options={[
                        {
                          label: 'All metadata',
                          value: '',
                          isAdvancedMetadata: false,
                        },
                        ...this.props.metadataOptions.map(o => ({
                          ...o,
                          isAdvancedMetadata: true,
                        })),
                      ]}
                      onChange={this._setSelectedCustomField}
                      сlearFieldsAfterChange={[
                        this.props.form.advancedMetadataKey === ''
                          ? 'generalMetadataValue'
                          : 'advancedMetadataValue',
                      ]}
                    />
                  </div>

                  <div className="PlaylistReport_FieldContainer">
                    {this._getFieldComponent(isDisabledAllContent)}
                  </div>
                </div>
              </div>
              <div>
                <TextField
                  label="Playlist title"
                  name="playlistTitle"
                  placeholder="Playlist title"
                  value={form['playlistTitle']}
                  onChange={this._handleInputChange}
                  disabled={isDisabledAllContent}
                />
              </div>
            </Row>
            <Row columnsSize={[4, 4, 4]} marginBottom>
              <div>
                <Label caption="Generated by user" />
                <ReportUser
                  disabled={isDisabledAllContent}
                  formKey="owner"
                  placeholder="Select user"
                />
              </div>
              <div>
                <Label caption="Sent to client" />
                <ReportContact
                  disabled={isDisabledAllContent}
                  formKey="sharedWith"
                  placeholder="Select client"
                />
              </div>
              <div>
                <Label caption="Featuring writer" />
                <ReportWriter
                  disabled={isDisabledAllContent}
                  formKey="writer"
                />
              </div>
            </Row>
          </PageContainer.Section>
          <PageContainer.Section
            disabled={isDisabledAllContent}
            title={
              <>
                Filters
                {isShowBadge && !isFeatureLimited && (
                  <>
                    {EN_SPACE}
                    <PricingBadge title={badgeTitle} />
                  </>
                )}
              </>
            }
          >
            <Row columnsSize={[4, 4, 4]} marginBottom>
              {channels.length > 0 && (
                <div>
                  <Label caption="Filter by channel" />
                  <ReportChannels
                    formKey="selectedChannels"
                    channels={channels}
                    placeholder="Select channels"
                    disabled={isFiltersDisabled}
                  />
                </div>
              )}
              <div>
                <Label caption="Filter by tag" />
                <ReportTags
                  formKey="selectedTags"
                  disabled={isFiltersDisabled}
                />
              </div>
              <div>
                <Label caption="Clients lists only" />
                <ReportSelect
                  formKey="isClientOnly"
                  disabled={isFiltersDisabled}
                  value={(this.props.form.isClientOnly || false).toString()}
                  onChangeMain={value => {}}
                  options={[
                    {
                      label: 'Yes',
                      value: true,
                    },
                    {
                      label: 'No',
                      value: false,
                    },
                  ]}
                />
              </div>
            </Row>
            <Row columnsSize={[4, 4, 4]} marginBottom>
              {territories.length > 0 && (
                <div>
                  <Label caption="Filter by territory" />
                  <ReportChannels
                    formKey="selectedTerritories"
                    channels={territories}
                    placeholder="Select territories"
                    disabled={isFiltersDisabled}
                  />
                </div>
              )}
            </Row>
          </PageContainer.Section>
          <PageContainer.Section
            disabled={isDisabledAllContent}
            title={
              <>
                More options
                {isShowBadge && (
                  <>
                    {EN_SPACE}
                    <PricingBadge title={badgeTitle} />
                  </>
                )}
              </>
            }
            description={
              <>
                For definitions of the different data types,{' '}
                <InlineLink
                  to="https://support.disco.ac/home/insights-guide#generating-reports"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  click here
                </InlineLink>
                .
              </>
            }
          >
            <Row columnsSize={[4, 4, 4]} marginBottom>
              <div>
                <ChoiceList
                  title="General options"
                  items={[
                    {
                      label: 'Include access',
                      value: 'access_stats',
                      disabled: isReportChannelsDisabled,
                      selected: visibleColumns.includes('access_stats'),
                    },
                    {
                      label: 'Include playlist tags',
                      value: 'tags',
                      disabled: isReportChannelsDisabled,
                      selected: visibleColumns.includes('tags'),
                    },
                    {
                      label: this.props.teamMember
                        ? 'Include team member'
                        : 'Include recipients',
                      value: 'shared_with',
                      disabled: isReportChannelsDisabled,
                      selected: visibleColumns.includes('shared_with'),
                    },
                    {
                      label: 'Include user who created the playlist',
                      value: 'creator',
                      disabled: isReportChannelsDisabled,
                      selected: visibleColumns.includes('creator'),
                    },
                  ]}
                  onChange={this._handleCheckboxChange}
                />
                <AdminTool
                  renderFn={() => (
                    <ReportCheckbox
                      disabled={isReportChannelsDisabled}
                      formKey="force_all_results_secret_backdoor"
                    >
                      Full report [admin]
                    </ReportCheckbox>
                  )}
                />
              </div>
              <div>
                <ChoiceList
                  title="Track information"
                  items={trackInformation}
                  onChange={this._handleCheckboxChange}
                />
                {!this.state.showAllCustomFields && (
                  <Button
                    className="PlaylistReport_ShowAll"
                    appearance="link"
                    caption="Show all"
                    onClick={() =>
                      this.setState({
                        showAllCustomFields: true,
                      })
                    }
                  />
                )}
              </div>
              <div>
                <ChoiceList
                  title="Writer information"
                  items={[
                    {
                      label: 'Name',
                      value: 'writer_name',
                      disabled: isReportChannelsDisabled,
                      selected: visibleColumns.includes('writer_name'),
                    },
                    {
                      label: 'Percentage',
                      value: 'writer_split',
                      disabled: isReportChannelsDisabled,
                      selected: visibleColumns.includes('writer_split'),
                    },
                    {
                      label: 'Publisher',
                      value: 'writer_publisher',
                      disabled: isReportChannelsDisabled,
                      selected: visibleColumns.includes('writer_publisher'),
                    },
                    {
                      label: 'PRO',
                      value: 'writer_pro',
                      disabled: isReportChannelsDisabled,
                      selected: visibleColumns.includes('writer_pro'),
                    },
                  ]}
                  onChange={this._handleCheckboxChange}
                />
              </div>
            </Row>
          </PageContainer.Section>
          <div className="Form_ButtonsContainer separated-small">
            <ButtonContainer>
              <Button
                disabled={isDisabledAllContent}
                progressIndicator={generating}
                caption="Preview report"
                progressCaption="Generating report"
                onClick={this._getPlaylistReport}
              />
              <ReportDownloadButton disabled={isDisabledAllContent} />
            </ButtonContainer>
          </div>
        </PageContainer>
        {generated ? this._renderResults() : null}
        {generationError ? this._renderError(generationError) : null}
      </React.Fragment>
    )
  }
}

export default PlaylistReport
